import type { ComputedRef, Ref } from 'vue'

import { useQuery } from '@tanstack/vue-query'
import { computed, toValue } from 'vue'

import type { ExoSetup } from '@/domains/fiscal/utils/exo-setup/buildExoSetup.types'

import { configurationTraceQueryKey } from '@/domains/configuration/composables/use-configuration-query-keys/useConfigurationQueryKeys'
import { callConfiguration } from '@/domains/configuration/services'
import { EXO_STATUS_KEY } from '@/domains/fiscal/types/consts/ExoStatus.consts'
import { buildExoSetup } from '@/domains/fiscal/utils/exo-setup/buildExoSetup'
import { useLocationsQuery } from '@/domains/locations/composables/use-locations-query/useLocationsQuery'
import { GC_TIMES, STALE_TIMES } from '@/support/query/constants'

interface UseExoSetup {
  setup: ComputedRef<ExoSetup | null>
  isLoading: ComputedRef<boolean>
  isError: ComputedRef<boolean>
}

/**
 * Data source for the top-bar EXO indicator: how EXO is configured at tenant scope and per
 * location. Two requests regardless of location count, both gated on `enabled` so nothing is
 * fetched until the popover is opened for the first time — `Tenant::isExo()` already runs on
 * every full page load in `HandleInertiaRequests::share()`, and nothing else fiscal belongs
 * on that path.
 */
const useExoSetup = (enabled: Ref<boolean>): UseExoSetup => {
  const traceQuery = useQuery({
    // No scope: this is what makes one request cover every location. See buildExoSetup.
    queryKey: configurationTraceQueryKey(EXO_STATUS_KEY, null),
    queryFn: async () => {
      const response = await callConfiguration().trace({ key: EXO_STATUS_KEY })
      // TanStack treats `undefined` as an error state — normalise an empty body to null.
      return response.data.value ?? null
    },
    enabled: computed(() => toValue(enabled)),
    staleTime: STALE_TIMES.LOW_CHURN,
    gcTime: GC_TIMES.DEFAULT
  })

  const locationsQuery = useLocationsQuery({ enabled })

  const setup = computed(() => {
    const trace = traceQuery.data.value
    if (!trace) return null

    return buildExoSetup(trace, locationsQuery.data.value ?? [])
  })

  return {
    setup,
    isLoading: computed(() => traceQuery.isLoading.value || locationsQuery.isLoading.value),
    isError: computed(() => traceQuery.isError.value || locationsQuery.isError.value)
  }
}

export { useExoSetup }
